import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

interface AccordionItemProps {
  question: string
  answer: string
  isOpen?: boolean
  onToggle?: () => void
}

export const AccordionItem = ({
  question,
  answer,
  isOpen = false,
  onToggle
}: AccordionItemProps) => {
  return (
    <div className="bg-white rounded-xl mb-4 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
      <button 
        className="w-full flex items-center justify-between px-8 py-6 bg-transparent border-none cursor-pointer text-left text-lg font-semibold text-gray-900 hover:bg-gray-50 transition-colors duration-200"
        onClick={onToggle}
      >
        <span>{question}</span>
        <ChevronDown 
          size={24} 
          className={`flex-shrink-0 transition-transform duration-200 text-primary ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-8 pb-6 text-gray-600 leading-relaxed">
              <p>{answer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

interface AccordionProps {
  items: Array<{ question: string; answer: string }>
  allowMultiple?: boolean
}

const Accordion = ({ 
  items, 
  allowMultiple = false 
}: AccordionProps) => {
  const [openItems, setOpenItems] = useState<number[]>([])

  const handleToggle = (index: number) => {
    if (allowMultiple) {
      setOpenItems(prev =>
        prev.includes(index)
          ? prev.filter(i => i !== index)
          : [...prev, index]
      )
    } else {
      setOpenItems(prev =>
        prev.includes(index) ? [] : [index]
      )
    }
  }

  return (
    <div>
      {items.map((item, index) => (
        <AccordionItem
          key={index}
          question={item.question}
          answer={item.answer}
          isOpen={openItems.includes(index)}
          onToggle={() => handleToggle(index)}
        />
      ))}
    </div>
  )
}

export default Accordion

